'use client'
import { Phone } from 'lucide-react'
import { formatPhone } from '@sifly/shared-utils'

type Props = {
  /** Public tenant phone, already gated by `show_public_phone` upstream. */
  phone: string | null | undefined
  /** The situation, in the dealer's voice. Rendered above the number. */
  message: string
}

/**
 * The dead end's escape hatch: a calm "call to book" line shown wherever the
 * calendar has nothing left to sell online.
 *
 * WHY this exists: online availability is NARROWER than real availability. A
 * session-mode product's booking cutoff (`cutoff_minutes`) closes web sales
 * hours or days before the session itself — AE's Lake Powell lesson uses 7 days
 * — so a day the calendar renders as open can hand back zero bookable times
 * while the dealer still has a boat, a guide, and room for two. "No times left
 * that day — try another date" was actively wrong there: the day is fine, only
 * the web checkout has closed. Rather than silently losing the booking, hand the
 * customer the phone number.
 *
 * WHY one component across three empty states (session day, session month,
 * daily month): a visitor who sees a phone number on one dead end and a shrug on
 * the next learns the store is inconsistent. Callers vary the `message`, never
 * the affordance.
 *
 * Renders NOTHING without a phone. A dealer with `show_public_phone` off has
 * said they don't take calls, and inventing a contact route they didn't publish
 * is worse than the plain empty state — so callers keep their own copy as the
 * fallback and this returns null.
 */
export function CallToBook({ phone, message }: Props) {
  if (!phone) return null
  return (
    <div className="text-center text-sm">
      <p className="text-[var(--color-text-muted)]">{message}</p>
      {/* `-readable`, not the raw fill token: --color-cta is chosen for punch
          with a label sitting ON it, and as text it fails AA on light brands
          (AE's #fa9f42 is 2.08:1 on white). The derived token keeps the hue. */}
      <a
        href={`tel:${phone.replace(/[^\d+]/g, '')}`}
        className="mt-1 inline-flex min-h-[44px] items-center gap-2 font-medium text-[var(--color-cta-readable)] hover:underline"
      >
        <Phone className="h-4 w-4" aria-hidden="true" />
        Call to book: {formatPhone(phone)}
      </a>
    </div>
  )
}
